import React from 'react';
import styled from '@emotion/styled';
import { Grid } from './Grid';
import breakpoints from './Theme/breakpoints';
import Headline3 from './Typography/Heading/Headline3';
import Button from './Button';
import colors from './Theme/colors';

const JobEbpSpotlightGrid = styled(Grid)`
  font-family: ${({ theme }) => theme.fonts.primary};
  &&& {
    padding: 0 24px;
  }
`;

const JobEbpSpotlightContainer = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
  height: auto;

  @media (min-width: ${breakpoints.md}px) {
    height: 256px;
  }
`;

const JobEbpSpotlightCallout = styled.div`
  display: flex;
  position: absolute;
  height: calc(100% - 16vw);
  flex-direction: column;
  justify-content: space-evenly;
  align-items: flex-start;
  padding: 8vw 10vw;
  overflow: visible;
  @media (min-width: ${breakpoints.sm}px) {
    flex-direction: row;
    padding: 4vw;
    height: calc(100% - 8vw);
  }
  @media (min-width: ${breakpoints.md}px) {
    padding: 36px 28px 36px 42px;
    align-items: flex-start;
    justify-content: space-between;
    flex-direction: column;
    height: 196px;
    position: static;
    background-color: ${({ theme }) => theme.colors.black};
    flex-grow: 1;
    flex-basis: auto;
  }
`;

const JobEbpSpotlightHeadline = styled(Headline3)`
  height: 100%;
  color: black;
  display: block;
  width: calc(100% - 24px);
  && {
    line-height: 8vw;
    font-size: 6vw;
  }
  @media (min-width: ${breakpoints.sm}px) {
    display: flex;
    justify-content: center;
    align-items: center;
    && {
      line-height: 4.8vw;
      font-size: 3.2vw;
    }
  }
  @media (min-width: ${breakpoints.md}px) {
    display: static;
    color: white;
    && {
      font-size: ${({ theme }) => theme.sizes.h3};
      line-height: 1.4;
    }
  }
`;

const JobEbpSpotlightLinkWrapper = styled.div`
  flex-basis: auto;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const JobEbpSpotlightButton = styled(Button)`
  color: white;
  border: none;
  text-transform: unset;
  width: fit-content;
  background-color: ${({ theme }) => theme.colors.black};
  margin: 2vw 0 0 0;
  font-size: 3vw;
  @media (min-width: ${breakpoints.sm}px) {
    margin: 0;
    font-size: 1.8vw;
    padding: 3vw 2vw;
  }
  @media (min-width: ${breakpoints.md}px) {
    margin: 20px 0;
    font-size: 14px;
    padding: 12px;
    background-color: ${({ theme }) => theme.colors.greyDark};
  }
  &:hover {
    border: none;
    background-color: ${colors.primaryFallback};
    background-color: ${colors.primary};
  }
`;

const JobEbpSpotlightImageContainer = styled.div`
  flex-grow: 1;
  position: relative;
  overflow: hidden;

  @media (min-width: ${breakpoints.md}px) {
    flex-basis: 320px;
    flex-shrink: 0;
  }
`;

const MobileImageWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;



  @media (min-width: ${breakpoints.sm}px) {
    display: none;
  }
`;

const TabletImageWrapper = styled.div`
  display: none;

  @media (min-width: ${breakpoints.sm}px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  @media (min-width: ${breakpoints.md}px) {
    display: none;
  }
`;

const DesktopImageWrapper = styled.div`
  display: none;

  > div {
    border-radius: 0;
  }

  @media (min-width: ${breakpoints.md}px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const JobEbpSpotlightImageOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 60%;
  background-color: ${({ theme }) => theme.colors.white};
  backdrop-filter: opacity(1%);
  @media (min-width: ${breakpoints.md}px) {
    background-color: transparent;
    opacity: unset;
    backdrop-filter: none;
  }
`;

interface JobEbpSpotlightProperties {
  ebpLink: React.ReactNode;
  epbCallout: string;
  mobileImage: React.ReactNode;
  tabletImage: React.ReactNode;
  desktopImage: React.ReactNode;
}

const JobEbpSpotlight = ({
  ebpLink,
  epbCallout,
  mobileImage,
  tabletImage,
  desktopImage,
}: JobEbpSpotlightProperties) => (
  <JobEbpSpotlightGrid spacing={0}>
    <JobEbpSpotlightContainer>
      <JobEbpSpotlightImageContainer>
        <MobileImageWrapper>{ mobileImage }</MobileImageWrapper>
        <TabletImageWrapper>{ tabletImage }</TabletImageWrapper>
        <DesktopImageWrapper>{ desktopImage }</DesktopImageWrapper>
        <JobEbpSpotlightImageOverlay />
      </JobEbpSpotlightImageContainer>
      <JobEbpSpotlightCallout>
        <JobEbpSpotlightHeadline tag="span">
          { epbCallout }
        </JobEbpSpotlightHeadline>
        <JobEbpSpotlightLinkWrapper>
          { ebpLink }
        </JobEbpSpotlightLinkWrapper>
      </JobEbpSpotlightCallout>
    </JobEbpSpotlightContainer>
  </JobEbpSpotlightGrid>
);

export default JobEbpSpotlight;
